import { assertNotEmpty, type Optional } from '@festina-life/core'
import { paperStyles, zIndexStyles } from '@festina-life/ui/libs'
import { PathBreadcrumb, type PathPart } from '@festina-life/ui/structure'
import cx from 'classnames'
import React, { useMemo } from 'react'

import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { sanitizePackageNameForLayer } from './helpers'
import type { ActiveConfigurationItem } from './types'
import { useScrollToPath } from './useScrollToPath'

type Props = {
  ref: React.RefObject<HTMLDivElement | null>
  activeConfigurationItem: Optional<ActiveConfigurationItem>
}

export const StickyActiveConfigurationItemPath = ({
  activeConfigurationItem,
  ref,
}: Props) => {
  const translateLayer = useTranslateLayer()
  const scrollToPath = useScrollToPath()

  const pathParts = useMemo(() => {
    const parts: PathPart[] = []

    if (!activeConfigurationItem) {
      return parts
    }

    const lastLayer = activeConfigurationItem?.layers.at(-1)
    assertNotEmpty(lastLayer)
    const lastLayerName = translateLayer(lastLayer)
    const sanitizedPackageNameParts = sanitizePackageNameForLayer({
      packageName: activeConfigurationItem?.packageName,
      layerName: lastLayerName,
    })?.split('.')

    parts.push(
      ...(activeConfigurationItem?.layers.map(
        (layer) =>
          ({
            label: translateLayer(layer),
            onClick: () => scrollToPath(layer),
          }) satisfies PathPart,
      ) ?? []),
    )

    parts.push(
      ...(sanitizedPackageNameParts?.map((part, partIndex) => ({
        label: part,
        onClick: () =>
          scrollToPath(
            lastLayer,
            sanitizedPackageNameParts.slice(0, partIndex + 1).join('.'),
          ),
      })) ?? []),
    )

    return parts
  }, [translateLayer, activeConfigurationItem, scrollToPath])

  return (
    <div
      ref={ref}
      className={cx(
        'sticky top-0 mb-1.5 w-full',
        paperStyles(),
        zIndexStyles('sticky'),
        { hidden: !pathParts.length },
      )}
    >
      <div className="flex items-center gap-x-1">
        <PathBreadcrumb parts={pathParts} showBorder={false} size="compact" />
      </div>
    </div>
  )
}
